import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MangaReader from '../components/MangaReader';
import API_BASE from '../config';

const MAX_BOOKS = 3;
const WEEKLY_LIMIT = 5;

const Library = () => {
  // =========================
  // AUTH STATE
  // =========================
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isRegister, setIsRegister] = useState(false);

  const [token, setToken] = useState(localStorage.getItem('token'));

  // =========================
  // DATA STATE
  // =========================
  const [books, setBooks] = useState([]);
  const [myBooks, setMyBooks] = useState([]);
  const [weeklyCount, setWeeklyCount] = useState(0);
  const [message, setMessage] = useState('');

  // ✅ FIX: store full book, not just ID
  const [readingBook, setReadingBook] = useState(null);

  const backend = API_BASE;

  // =========================
  // HELPERS
  // =========================
  const isActive = (expiresAt) => new Date(expiresAt) > new Date();

  const getTimeRemaining = (expiresAt) => {
    const total = new Date(expiresAt) - new Date();
    if (total <= 0) return { text: 'Expired', color: 'text-red-500' };

    const seconds = Math.floor((total / 1000) % 60);
    const minutes = Math.floor((total / 1000 / 60) % 60);
    const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
    const days = Math.floor(total / (1000 * 60 * 60 * 24));

    return {
      text: `${days}d ${hours}h ${minutes}m ${seconds}s`,
      color: days < 1 ? 'text-red-500' : 'text-green-500'
    };
  };

  // =========================
  // AUTH HANDLER
  // =========================
  const handleAuth = async () => {
    try {
      const endpoint = isRegister
        ? `${backend}/auth/register`
        : `${backend}/auth/login`;

      const payload = isRegister
        ? { name, email, password }
        : { email, password };

      const res = await axios.post(endpoint, payload);

      if (res.data?.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        setToken(res.data.token);
        setMessage(isRegister ? 'Account created!' : 'Logged in!');
      } else {
        setMessage('Success');
      }
    } catch (err) {
      console.error('AUTH ERROR:', err.response || err);
      setMessage(err.response?.data?.message || 'Auth failed');
    }
  };

  // =========================
  // FETCH
  // =========================
  const fetchBooks = async () => {
    const res = await axios.get(`${backend}/books`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setBooks(res.data);
  };

  const fetchMyBooks = async () => {
    const res = await axios.get(`${backend}/books/my`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    setWeeklyCount(res.data.weeklyCount || 0);
    setMyBooks(res.data.books || res.data);
  };

  useEffect(() => {
    if (token) {
      fetchBooks();
      fetchMyBooks();
    }
  }, [token]);

  // =========================
  // ACTIONS
  // =========================
  const checkout = async (bookId) => {
    try {
      const res = await axios.post(
        `${backend}/books/checkout`,
        { bookId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage(`Expires ${new Date(res.data.expiresAt).toLocaleString()}`);
      fetchBooks();
      fetchMyBooks();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Checkout failed');
    }
  };

  const downloadBook = async (bookId) => {
    const res = await fetch(`${backend}/download/${bookId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!res.ok) return setMessage('Download failed');

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `book-${bookId}`;
    a.click();
  };

  // =========================
  // DERIVED
  // =========================
  const activeMyBooks = myBooks.filter(book => isActive(book.expiresAt));

  // =========================
  // 📖 READER MODE
  // =========================
  if (readingBook) {
    return (
      <MangaReader
        bookId={readingBook.id}
        token={token}
        totalPages={readingBook.pageCount || 1}
        onClose={() => setReadingBook(null)}
      />
    );
  }

  // =========================
  // LOGIN UI
  // =========================
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="bg-white p-8 rounded-xl shadow-xl w-full max-w-md">
          <h1 className="text-2xl font-bold mb-4 text-center">
            {isRegister ? 'Register' : 'Login'}
          </h1>

          {isRegister && (
            <input
              className="w-full border p-2 mb-3"
              placeholder="Name"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          )}

          <input
            className="w-full border p-2 mb-3"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
          />

          <input
            type="password"
            className="w-full border p-2 mb-3"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />

          <button
            onClick={handleAuth}
            className="w-full bg-primary text-white py-2 rounded"
          >
            {isRegister ? 'Register' : 'Login'}
          </button>

          <button
            onClick={() => setIsRegister(!isRegister)}
            className="w-full mt-3 text-sm text-primary"
          >
            Toggle Register/Login
          </button>

          <p className="text-red-500 mt-3">{message}</p>
        </div>
      </div>
    );
  }

  // =========================
  // MAIN LIBRARY UI
  // =========================
  return (
    <div className="bg-background min-h-screen">

      <div className="py-10 text-center">
        <h1 className="text-4xl font-bold">
          DIGITAL LIBRARY
        </h1>
      </div>

      <div className="container mx-auto px-4">

        {/* STATUS */}
        <div className="mb-6">
          <p>📚 {activeMyBooks.length} / {MAX_BOOKS}</p>
          <p>📆 {weeklyCount} / {WEEKLY_LIMIT} this week</p>
          <p className="text-red-500">{message}</p>
        </div>

        {/* BOOK GRID */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {books.map(book => (
            <div key={book.id} className="border p-4 rounded">

              <h3 className="font-bold">{book.title}</h3>

              <div className="flex gap-2 mt-3">

                {book.availableCopies > 0 &&
                  activeMyBooks.length < MAX_BOOKS &&
                  weeklyCount < WEEKLY_LIMIT && (
                    <button
                      onClick={() => checkout(book.id)}
                      className="bg-primary text-white px-2 py-1 rounded"
                    >
                      Borrow
                    </button>
                  )}

                <button
                  onClick={() => setReadingBook(book)}
                  className="border px-2 py-1 rounded"
                >
                  Read
                </button>

                <button
                  onClick={() => downloadBook(book.id)}
                  className="border px-2 py-1 rounded"
                >
                  DL
                </button>

              </div>

            </div>
          ))}
        </div>

        {/* MY BOOKS */}
        <div className="mt-10">
          <h2 className="text-xl font-bold">My Books</h2>

          {activeMyBooks.map(book => {
            const timer = getTimeRemaining(book.expiresAt);

            return (
              <div key={book.bookId} className="border p-3 mt-2 rounded">
                <strong>{book.title}</strong>
                <div className={timer.color}>{timer.text}</div>

                <button
                  onClick={() =>
                    setReadingBook({
                      id: book.bookId,
                      pageCount: book.pageCount
                    })
                  }
                  className="mt-2 bg-primary text-white px-3 py-1 rounded"
                >
                  Read
                </button>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default Library;